import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { User, Mail, Lock, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function ProfilePage() {
  const { user, updateProfile, changePassword } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileError('');
    setProfileSuccess('');
    
    const result = await updateProfile({ name });
    
    if (result.success) {
      setProfileSuccess('Profile updated successfully');
    } else {
      setProfileError(result.error);
    }
    
    setProfileLoading(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    
    setPasswordLoading(true);
    
    const result = await changePassword(currentPassword, newPassword);
    
    if (result.success) {
      setPasswordSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setPasswordError(result.error);
    }
    
    setPasswordLoading(false);
  };

  return (
    <div className="p-8" data-testid="profile-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Profile Settings
        </h1>
        <p className="text-[#71717A]">Manage your account information</p>
      </div>

      <div className="max-w-2xl space-y-8">
        {/* Profile Information */}
        <div className="bg-[#141417] border border-[#27272A] rounded-md p-6">
          <h2 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Profile Information
          </h2>
          
          {profileSuccess && (
            <div className="bg-[#00FF66]/10 border border-[#00FF66]/30 rounded-sm p-3 mb-4 flex items-center gap-2">
              <Check className="w-4 h-4 text-[#00FF66]" />
              <span className="text-[#00FF66] text-sm">{profileSuccess}</span>
            </div>
          )}
          
          {profileError && (
            <div className="bg-[#FF3333]/10 border border-[#FF3333]/30 rounded-sm p-3 mb-4 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-[#FF3333]" />
              <span className="text-[#FF3333] text-sm">{profileError}</span>
            </div>
          )}
          
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div>
              <Label className="text-[#A1A1AA]">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                <Input
                  value={user?.email || ''}
                  className="input-dark pl-10"
                  disabled
                />
              </div>
              <p className="text-[#71717A] text-xs mt-1">Email cannot be changed</p>
            </div>
            
            <div>
              <Label className="text-[#A1A1AA]">Full Name</Label>
              <div className="relative mt-2">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-dark pl-10"
                  data-testid="profile-name-input"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="bg-[#27272A] rounded-sm px-3 py-1">
                <span className="text-[#A1A1AA] text-sm">Role: </span>
                <span className="text-white text-sm font-medium capitalize">{user?.role}</span>
              </div>
              <div className="bg-[#27272A] rounded-sm px-3 py-1">
                <span className="text-[#A1A1AA] text-sm">Courses: </span>
                <span className="text-white text-sm font-medium">{user?.enrolled_courses?.length || 0}</span>
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="btn-primary"
              disabled={profileLoading}
              data-testid="update-profile-btn"
            >
              {profileLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </div>

        {/* Change Password */}
        <div className="bg-[#141417] border border-[#27272A] rounded-md p-6">
          <h2 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Change Password
          </h2>
          
          {passwordSuccess && (
            <div className="bg-[#00FF66]/10 border border-[#00FF66]/30 rounded-sm p-3 mb-4 flex items-center gap-2">
              <Check className="w-4 h-4 text-[#00FF66]" />
              <span className="text-[#00FF66] text-sm">{passwordSuccess}</span>
            </div>
          )}
          
          {passwordError && (
            <div className="bg-[#FF3333]/10 border border-[#FF3333]/30 rounded-sm p-3 mb-4 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-[#FF3333]" />
              <span className="text-[#FF3333] text-sm">{passwordError}</span>
            </div>
          )}
          
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <Label className="text-[#A1A1AA]">Current Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="input-dark pl-10"
                  required
                  data-testid="current-password-input"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-[#A1A1AA]">New Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input-dark pl-10"
                  required
                  data-testid="new-password-input"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-[#A1A1AA]">Confirm New Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-dark pl-10"
                  required
                  data-testid="confirm-new-password-input"
                />
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="btn-primary"
              disabled={passwordLoading}
              data-testid="change-password-btn"
            >
              {passwordLoading ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
